import { normalizeCollection } from './client'
import type { Collection, Equipment, ImportIssue, MaintenancePlan, PprDocument } from './types'

type UnknownRecord = Record<string, unknown>
const record = (value: unknown): UnknownRecord => value && typeof value === 'object' ? value as UnknownRecord : {}
const numberValue = (value: unknown, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const stringValue = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback

export function normalizeDocument(value: unknown): PprDocument {
  const source = record(value)
  const metrics = record(source.metrics)
  return {
    ...(source as unknown as PprDocument),
    id: stringValue(source.id),
    status: stringValue(source.status, 'QUEUED'),
    createdAt: stringValue(source.createdAt, new Date().toISOString()),
    metrics: {
      totalRows: numberValue(metrics.totalRows ?? metrics.totalRowCount),
      activityRows: numberValue(metrics.activityRows ?? metrics.activityRowCount),
      equipmentRows: numberValue(metrics.equipmentRows ?? metrics.equipmentRowCount),
      autoProcessedRows: numberValue(metrics.autoProcessedRows ?? metrics.autoProcessedRowCount),
      issueCount: numberValue(metrics.issueCount),
      blockerCount: numberValue(metrics.blockerCount),
      issueRows: numberValue(metrics.issueRows ?? metrics.issueRowCount),
      reviewRequiredRows: numberValue(metrics.reviewRequiredRows),
      monthMarkerCount: numberValue(metrics.monthMarkerCount),
      autoPercent: numberValue(metrics.autoPercent ?? metrics.autoProcessedPercent),
    },
  }
}

export function normalizeIssue(value: unknown): ImportIssue {
  const source = record(value)
  const resolution = record(source.resolution)
  return {
    ...(source as unknown as ImportIssue),
    id: stringValue(source.id),
    code: stringValue(source.code, 'REVIEW_REQUIRED'),
    severity: stringValue(source.severity, 'WARNING'),
    status: stringValue(source.status, 'OPEN'),
    message: stringValue(source.message ?? source.reason, 'Проверьте значение'),
    field: stringValue(source.field) || null,
    sourceColumn: stringValue(source.sourceColumn ?? source.field) || null,
    suggestedValue: source.suggestedValue ?? source.suggestion,
    normalizedValue: source.normalizedValue ?? resolution.normalizedValue,
    comment: stringValue(source.comment ?? resolution.comment) || null,
    sourceRow: source.sourceRow === null || source.sourceRow === undefined ? null : numberValue(source.sourceRow),
  }
}

export function normalizeEquipment(value: unknown): Equipment {
  const source = record(value)
  return {
    ...(source as unknown as Equipment),
    id: stringValue(source.id),
    revision: numberValue(source.revision),
    name: stringValue(source.name, 'Без названия'),
    code: stringValue(source.code ?? source.externalKey) || null,
    externalKey: stringValue(source.externalKey ?? source.code) || null,
    unit: stringValue(source.unit ?? source.unitRaw) || null,
    status: source.active === true ? 'ACTIVE' : source.active === false ? 'INACTIVE' : stringValue(source.status),
    active: typeof source.active === 'boolean' ? source.active : source.status !== 'INACTIVE' && source.status !== 'ARCHIVED',
    location: stringValue(source.location ?? record(source.system).name) || null,
  }
}

export function normalizePlan(value: unknown): MaintenancePlan {
  const source = record(value)
  const items = Array.isArray(source.items) ? source.items : []
  const rawSourceDocument = record(source.sourceDocument)
  const sourceDocument = stringValue(rawSourceDocument.id) ? {
    id: stringValue(rawSourceDocument.id),
    version: numberValue(rawSourceDocument.version, 1),
    year: rawSourceDocument.year === null || rawSourceDocument.year === undefined ? null : numberValue(rawSourceDocument.year),
    ...(rawSourceDocument.kind ? { kind: stringValue(rawSourceDocument.kind) as 'ANNUAL' | 'MONTHLY' } : {}),
    ...(rawSourceDocument.month !== undefined ? { month: rawSourceDocument.month === null ? null : numberValue(rawSourceDocument.month) } : {}),
  } : null
  return {
    ...(source as unknown as MaintenancePlan),
    id: stringValue(source.id),
    year: numberValue(source.year, new Date().getFullYear()),
    month: numberValue(source.month, new Date().getMonth() + 1),
    sourceDocument,
    items: items as MaintenancePlan['items'],
    itemCount: numberValue(source.itemCount, items.length),
  }
}

export function normalizeDocumentsCollection(payload: unknown, page = 1, limit = 25): Collection<PprDocument> {
  const collection = normalizeCollection<unknown>(payload, page, limit)
  return { ...collection, items: collection.items.map(normalizeDocument) }
}

export function normalizeIssuesCollection(payload: unknown, page = 1, limit = 200): Collection<ImportIssue> {
  const collection = normalizeCollection<unknown>(payload, page, limit)
  return { ...collection, items: collection.items.map(normalizeIssue) }
}

export function normalizeEquipmentCollection(payload: unknown, page = 1, limit = 25): Collection<Equipment> {
  const collection = normalizeCollection<unknown>(payload, page, limit)
  return { ...collection, items: collection.items.map(normalizeEquipment) }
}

export function normalizePlanCollection(payload: unknown, page = 1, limit = 20): Collection<MaintenancePlan> {
  const collection = normalizeCollection<unknown>(payload, page, limit)
  return { ...collection, items: collection.items.map(normalizePlan) }
}
